import { Alert, Box, Button, Center, Group, Loader, Paper, Stack, Title } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconLogout } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { Outlet } from 'react-router-dom';
import { useNameGate, type NameForm } from '../hooks/useNameGate';
import { NameFields } from './NameFields';

export function NameGate() {
  const auth = useAuth();
  const { status, form } = useNameGate();

  if (status === 'open') return <Outlet />;

  if (status === 'waiting') {
    return (
      <Center h="100vh" role="progressbar" aria-label="Loading">
        <Loader size="xl" />
      </Center>
    );
  }

  return <NamePrompt form={form} onSignOut={() => auth.signOut()} />;
}

// The "What's your name?" page, shared with the post-sign-in bypass (PostSignin).
export function NamePrompt({ form, onSignOut }: Readonly<{ form: NameForm; onSignOut: () => void }>) {
  return (
    <Box style={{ minHeight: '100vh', background: 'var(--bg-canvas)', display: 'flex', flexDirection: 'column' }}>
      <Group justify="space-between" align="center" style={{ padding: '24px 32px' }}>
        <img src="/cloudhouse-logo.png" alt="Cloudhouse" style={{ height: 30, width: 'auto', objectFit: 'contain' }} />
        <Button variant="subtle" color="gray" leftSection={<IconLogout size={16} stroke={1.8} />} onClick={onSignOut}>
          Sign out
        </Button>
      </Group>
      <Center style={{ flex: 1, padding: '24px' }}>
        <Paper style={{
          width: '100%', maxWidth: 460, background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)', borderRadius: 18, padding: 40,
        }}>
          <Stack gap={20}>
            <Title order={2} style={{ color: 'var(--text-default)', fontSize: 22 }}>What's your name?</Title>
            {form.error && (
              <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">{form.error}</Alert>
            )}
            <NameFields
              firstName={form.firstName}
              lastName={form.lastName}
              onFirstNameChange={form.setFirstName}
              onLastNameChange={form.setLastName}
              firstNameError={form.firstNameError}
              lastNameError={form.lastNameError}
              disabled={form.saving}
            />
            <Button
              size="md" radius={10} fullWidth fw={700}
              rightSection={<IconArrowRight size={16} stroke={2.2} />}
              disabled={!form.canSubmit}
              loading={form.saving}
              onClick={form.submit}
            >
              Continue
            </Button>
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
